import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  FacebookLogo,
  XLogo,
  TiktokLogo,
  InstagramLogo,
  Link as LinkIcon,
  ShareNetwork,
} from "@phosphor-icons/react";
import { trackEvent } from "@/lib/analytics";

/**
 * Reusable share panel — used on /share and (later) inside the newsletter
 * confirmation flow. Each channel:
 *   1. Fires `share_click` analytics event tagged with the channel
 *   2. Opens the platform-native share dialog
 * Instagram doesn't expose a web share URL, so IG only surfaces via the
 * Web Share API on mobile (which lists IG, Messages, WhatsApp, etc.).
 */

// Message the sharer will see pre-filled. Kept short so it flies on X.
const SHARE_TEXT =
  "I just downloaded Stable-ish — a clinical assistant built for ER & ICU nurses. 14-day free trial:";

// Landing-page origin, computed at render time so it works on preview + prod.
function buildShareUrl(channel) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://stableish.me";
  const params = new URLSearchParams({
    utm_source: "user_share",
    utm_medium: channel,
    utm_campaign: "nurse_referral",
  });
  return `${origin}/?${params.toString()}`;
}

export default function ShareCard({ testIdPrefix = "share" }) {
  const [nativeSupported] = useState(
    () => typeof navigator !== "undefined" && typeof navigator.share === "function"
  );

  const shareUrls = useMemo(
    () => ({
      instagram: buildShareUrl("instagram"),
      facebook: buildShareUrl("facebook"),
      x: buildShareUrl("x"),
      tiktok: buildShareUrl("tiktok"),
      copy: buildShareUrl("copy_link"),
      native: buildShareUrl("native_share"),
    }),
    []
  );

  const openWindow = (url) => {
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=680");
  };

  const shareFacebook = () => {
    trackEvent("share_click", "share-card", { channel: "facebook" });
    openWindow(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrls.facebook)}`
    );
  };

  const shareX = () => {
    trackEvent("share_click", "share-card", { channel: "x" });
    openWindow(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrls.x)}&text=${encodeURIComponent(SHARE_TEXT)}`
    );
  };

  // Instagram has no web share URL. Same pattern as TikTok: copy the
  // caption+link to clipboard, then open Instagram. On mobile the OS
  // hands off to the IG app; on desktop instagram.com opens.
  const shareInstagram = async () => {
    trackEvent("share_click", "share-card", { channel: "instagram" });
    const caption = `${SHARE_TEXT} ${shareUrls.instagram}`;
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copied. Paste it into your Instagram story or post.");
    } catch {
      toast.error("Couldn't copy — long-press the link below.");
    }
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };

  // TikTok has no web share intent, and /upload shows "Feature
  // unavailable" for logged-out or mobile-web users. Opening the domain
  // root instead: on mobile the OS hands off to the installed TikTok app
  // (universal link), on desktop it opens tiktok.com. Caption is copied
  // to clipboard beforehand so the user pastes into their post/story.
  const shareTikTok = async () => {
    trackEvent("share_click", "share-card", { channel: "tiktok" });
    const caption = `${SHARE_TEXT} ${shareUrls.tiktok}`;
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copied. Opening TikTok — paste it in your post.");
    } catch {
      toast.error("Couldn't copy — long-press the link below.");
    }
    window.open("https://www.tiktok.com/", "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    trackEvent("share_click", "share-card", { channel: "copy_link" });
    try {
      await navigator.clipboard.writeText(shareUrls.copy);
      toast.success("Link copied. Paste it anywhere.");
    } catch {
      toast.error("Couldn't copy. Long-press the link below to copy.");
    }
  };

  const nativeShare = async () => {
    trackEvent("share_click", "share-card", { channel: "native" });
    try {
      await navigator.share({
        title: "Stable-ish — clinical assistant for ER & ICU nurses",
        text: SHARE_TEXT,
        url: shareUrls.native,
      });
    } catch {
      /* user cancelled — silent */
    }
  };

  const btn =
    "group flex items-center justify-center gap-3 border border-white/15 hover:border-[#FF2A2A] hover:bg-[#FF2A2A]/[0.06] text-white/85 hover:text-white px-5 py-4 transition-colors";
  const btnPrimary =
    "group flex items-center justify-center gap-3 border border-[#FF2A2A] bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white px-5 py-4 transition-colors";

  return (
    <div
      className="relative bg-black border border-white/10 p-6 md:p-8 noise"
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-4">
        // tell a nurse friend
      </div>
      <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight leading-tight mb-2">
        Know a nurse who&apos;d love this?
      </h3>
      <p className="text-sm text-white/60 max-w-md">
        Word from another nurse is the best signal. Share Stable-ish with
        someone on your unit — they get 14 days free, no strings.
      </p>

      <div className="mt-7 grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={shareInstagram}
          className={`${btnPrimary} sm:col-span-2`}
          data-testid={`${testIdPrefix}-instagram`}
          aria-label="Share on Instagram"
        >
          <InstagramLogo size={20} weight="bold" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
            Share on Instagram
          </span>
        </button>
        <button
          type="button"
          onClick={shareFacebook}
          className={btn}
          data-testid={`${testIdPrefix}-facebook`}
          aria-label="Share on Facebook"
        >
          <FacebookLogo size={20} weight="bold" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
            Share on Facebook
          </span>
        </button>
        <button
          type="button"
          onClick={shareX}
          className={btn}
          data-testid={`${testIdPrefix}-x`}
          aria-label="Share on X"
        >
          <XLogo size={20} weight="bold" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
            Share on X
          </span>
        </button>
        <button
          type="button"
          onClick={shareTikTok}
          className={btn}
          data-testid={`${testIdPrefix}-tiktok`}
          aria-label="Share on TikTok"
        >
          <TiktokLogo size={20} weight="bold" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
            Share on TikTok
          </span>
        </button>
        <button
          type="button"
          onClick={copyLink}
          className={btn}
          data-testid={`${testIdPrefix}-copy`}
          aria-label="Copy share link"
        >
          <LinkIcon size={20} weight="bold" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
            Copy link
          </span>
        </button>
        {nativeSupported ? (
          <button
            type="button"
            onClick={nativeShare}
            className={btn}
            data-testid={`${testIdPrefix}-native`}
            aria-label="Share via device"
          >
            <ShareNetwork size={20} weight="bold" />
            <span className="font-mono text-[11px] uppercase tracking-[0.22em]">
              More — Instagram, Messages…
            </span>
          </button>
        ) : (
          <div
            className="hidden sm:flex items-center justify-center border border-dashed border-white/10 px-5 py-4 text-[10px] font-mono uppercase tracking-[0.22em] text-white/35"
            data-testid={`${testIdPrefix}-native-fallback`}
          >
            Open on mobile for Instagram + Messages
          </div>
        )}
      </div>

      <div className="mt-6 pt-5 border-t border-white/10">
        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 mb-2">
          direct link
        </div>
        <div
          className="font-mono text-[11px] text-white/70 break-all select-all"
          data-testid={`${testIdPrefix}-raw-url`}
        >
          {shareUrls.copy}
        </div>
      </div>
    </div>
  );
}
